import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { capitalizeFirstLetter } from 'src/utils';
import { notifications } from '@mantine/notifications';
import { ReactComponent as IconError } from "src/assets/icons/toast-error.svg";
import { ReactComponent as IconSuccess } from "src/assets/icons/toast-success.svg";
import { Text } from '@mantine/core';
import useCleverTapEvents from 'src/events/useGetEvents';
import { LIVELINESS_EVENT } from 'src/events/constant';
import { stepAndRouteMap } from 'src/constants';
import { ScreenWrapper } from '../../components';
import { OnboardingProgress } from '../../components/onboardingProgress';
import { RootState } from '../../redux/store';
import { useCommonStyles } from '../../styles/commonStyles';
import {
  clearDigioFormError, getDigioImageVerifyCredentials, setDigioFormStatus, verifyDigioImageVerification,
} from './digioFormSlice';
import { initDigio } from './initDigio';
import { useStyles } from './personalImageVerification.styles';
import faceIcon from "../../assets/icons/selfie-face.svg";
import glassesIcon from "../../assets/icons/selfie-glasses.svg";
import cameraIcon from "../../assets/icons/selfie-camera.svg";
import { ScreenLoader } from '../../components/screenLoader';
import { getProgressStepper } from '../../utils/helpers';
import { Button } from '../../components/atoms';

export const PersonalImageVerification = () : JSX.Element => {
  const { classes } = useStyles();
  const { commonClasses } = useCommonStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const digioState = useSelector((state: RootState) => state.digioData);
  const currentStep = useSelector((state:RootState) => state.account.userDetails.currentStep);
  const idRef = useRef<any>();
  const { TriggerEvent } = useCleverTapEvents();

  useEffect(() => {
    dispatch(setDigioFormStatus('loading'));
    idRef.current = setTimeout(() => {
      dispatch(getDigioImageVerifyCredentials());
    }, 500);
    return () => {
      clearTimeout(idRef.current);
    };
  }, []);

  const digioCallback = (response: any) => {
    if (response?.error_code) {
      notifications.show({
        color: 'red',
        message: capitalizeFirstLetter(response?.message) || 'Something went wrong, Try again!',
        icon: <IconError />,
      });
    } else {
      dispatch(verifyDigioImageVerification({ requestId: digioState?.imageVerificationCredentials?.requestId }));
    }
  };

  useEffect(() => {
    if (digioState.status === 'success') {
      if (digioState.isImageVerificationStarted && digioState.isImageVerified) {
        notifications.show({
          color: 'green',
          message: 'Image verified successfully!',
          icon: <IconSuccess />,
        });
        dispatch(setDigioFormStatus('idle'));
        const nextStep = stepAndRouteMap[currentStep][0];
        navigate(nextStep);
        TriggerEvent(LIVELINESS_EVENT.livelinessSuccess, {})
      }
      dispatch(setDigioFormStatus('idle'));
    } else if (digioState.status === 'error') {
      dispatch(clearDigioFormError());
      notifications.show({
        color: 'red',
        message: digioState.error.message || 'Something went wrong, Try again!',
        icon: <IconError />,
      });
    }
  }, [digioState.status]);

  const onNextClick = () => {
    if (digioState.isImageVerificationStarted && digioState?.imageVerificationCredentials) {
      initDigio({
        callback: digioCallback,
        identifier: digioState?.imageVerificationCredentials?.identifier,
        requestId: digioState?.imageVerificationCredentials?.requestId,
        token: digioState?.imageVerificationCredentials?.token,
      });
    } else {
      notifications.show({
        color: 'red',
        message: 'Something went wrong, Try again!',
        icon: <IconError />,
      });
    }
  };
  return (
    <ScreenWrapper>
      <div className="csMainContainer">
        <div className="csHeader">
          <div className="csProgressWrapper">
            <OnboardingProgress progress={getProgressStepper(currentStep)} />
          </div>
        </div>
        <div className={classes.contentContainer}>
          <h2 className="csHeading">
            Take a selfie
          </h2>
          <Text mb="xl" className="content_sm_regular text_fg_subtle">
            This is for verification purpose.
          </Text>
          <div className={classes.measuresWrapper}>
            <div className={classes.measuresContainer}>
              <img src={faceIcon} alt="face" />
              <p className="content_sm_regular text_fg_default">Please keep a straight face.</p>
            </div>
            <div className={classes.measuresContainer}>
              <img src={glassesIcon} alt="glasses" />
              <p className="content_sm_regular text_fg_default">
                Wearing glasses? Make sure we can clearly see your eyes.
              </p>
            </div>
            <div className={classes.measuresContainer}>
              <img src={cameraIcon} alt="camera" />
              <p className="content_sm_regular text_fg_default">
                Kindly Allow Access to your location and camera on this browser for verification.
              </p>
            </div>
          </div>
        </div>
        <div className="csFooter">
          <Button
            type="button"
            className={(commonClasses.button)}
            onClick={onNextClick}
          >
            Open Camera
          </Button>
        </div>
      </div>
      {digioState.status === 'loading' ? (<ScreenLoader />) : null}
    </ScreenWrapper>
  );
};

export default PersonalImageVerification;
